import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ActionUploadDownloadIterface } from "@components/ModalDownloadUploadCst/types";
import Table from "@components/Table";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { downloadFile, uploadFile } from "@utils/utilsApi";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { ActionsType } from "src/interfaces/Common";
import { KeyURL, MethodType } from "src/services/type";
import { fetcher } from "src/services/config";
import useSWRMutation from "swr/mutation";
import AnimationFadeIn from "@components/AnimationFadeIn";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";

interface AccountingCostProps {
  accounting: string;
  handleTabChange: any;
}

const AccountingCost: React.FC<AccountingCostProps> = ({
  accounting,
  handleTabChange,
}) => {
  const { id } = useParams();
  const { t } = useTranslation(["accounting-page"]);
  const navigate = useNavigate();
  const [openUpload, setOpenUpload] = useState<ActionUploadDownloadIterface>();
  const [idCostDetailDeleted, setIdCostDetailDeleted] = useState<string>();
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();
  const location = useLocation();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["projects.cost-detail"]}`;

  /*Function to call api to get costs data*/
  const { columns, dataTable, actionsTopToolbar, actionForRow, mutate } =
    useFetchTable(
      `${ServicesURL.getCostModelDetail}?accounting=true&managementType=${accounting}&corporateId=${id}`,
      PagesID["projects.cost-detail"],
      filters[filterId],
    );

  /*Trigger to delete a detail cost*/
  const { trigger: triggerDeleteCostDetail } = useSWRMutation(
    `${ServicesURL.postCostModelDetail}/${idCostDetailDeleted}?corporateId=${id}`,
    (url: string) => {
      return fetcher<any>(url, MethodType.DELETE);
    },
  );

  /*Function to handle the delete acition*/
  const handleDelete = React.useCallback(async () => {
    if (idCostDetailDeleted) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerDeleteCostDetail();
        const newData = [
          ...dataTable.output.filter(
            (el: any) => el.id !== idCostDetailDeleted,
          ),
        ];
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setIdCostDetailDeleted(undefined);
        setOpenToast(undefined);
        console.error(error);
      }
    }
  }, [dataTable, idCostDetailDeleted, mutate, triggerDeleteCostDetail]);

  /*Formatting the data from server*/
  const dataFormat = useMemo(() => {
    if (!dataTable?.output) return [];

    return dataTable.output.map((el: any) => {
      if (el.supplier) {
        el.supplier.label = el.supplier?.business_name || "-";
      }

      if (el.contract) {
        el.contract.label = el.contract?.sap_code || "-";
      }

      if (el.extra_data) {
        el.cost_model_item_account =
          el.extra_data?.cost_model_item_account || "-";
      }

      if (el.extra_data) {
        el.project_manager = el.extra_data?.project_manager || "-";
      }

      return el;
    });
  }, [dataTable?.output]);

  useEffect(() => {
    if (!idCostDetailDeleted) return;
  }, [idCostDetailDeleted]);

  const filteredColumns = useMemo(() => {
    if (columns) {
      return columns.map((el) => {
        if (el.accessorKey === "status") {
          return {
            ...el,
            optionsKey: "status_project_cost_accounting",
          };
        }
        return { ...el };
      });
    }
  }, [columns]);

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "delete": {
          return {
            ...action,
            callBack: (id: string) => {
              setIdCostDetailDeleted(id);
              setOpenModalDelete(true);
            },
          };
        }
        case "edit": {
          return {
            ...action,
            callBack: (data: any) => {
              navigate(
                `${location.pathname}/accounting-cost/${data.management_type}/${data.extra_data.project_id}/${data.extra_data.cost_model_item_id}/${data.id}`,
              );
            },
          };
        }
        case "button": {
          switch (action.key) {
            case "goods-receipt":
              return {
                ...action,
                getIcon: () => <ShoppingCartIcon />,
                callBack: (data: any) => {
                  handleTabChange(2, data.oda_sap_code || "");
                },
                disabled: true,
              };
            case "request-cost":
              return {};
            default:
              return action;
          }
        }
        default:
          return action;
      }
    });
  }, [actionForRow, navigate]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    configRowNew = configRowNew.map((el: ActionsType) => {
      /*Disable delete if not deletable*/
      if (el.type === "delete" && !row.original?.deletable) {
        return { ...el, disabled: true };
      }

      /*Button goods-receipt only if status is ISSUED*/
      if (
        el.type === "button" &&
        el.key == "goods-receipt" &&
        row.original.status == "ISSUED"
      ) {
        return { ...el, disabled: false };
      }

      return { ...el };
    });

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    return actionsTopToolbar
      ?.filter((action) => action.type !== "add")
      .map((action: ActionsType) => {
        switch (action.type) {
          case "upload": {
            const actionModal: ActionUploadDownloadIterface = {
              ...action,
              ...{
                service: `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
                types: ["upload", "download", "template"],
                onDownload: (el) =>
                  downloadFile(
                    `${ServicesURL[action.service as KeyURL]}/download${
                      el ? `/${el}` : ""
                    }?corporate_id=1&type=LEARNER` || "",
                  ),
                onUpload: (formData: FormData) => {
                  return uploadFile(
                    `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
                    formData,
                  );
                },
              },
            };
            return {
              ...action,
              ...{ callBack: () => setOpenUpload(actionModal) },
            };
          }
          case "visibility": {
            const actionModal: ActionUploadDownloadIterface = {
              ...action,
              ...{
                service: `${
                  ServicesURL[action.service as KeyURL]
                }?corporate_id=1`,
                types: ["upload", "download", "template"],
                onDownload: (el) =>
                  downloadFile(
                    `${ServicesURL[action.service as KeyURL]}/?corporate_id=1` ||
                      "",
                  ),
                onUpload: (formData: FormData) =>
                  uploadFile(
                    `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
                    formData,
                  ),
              },
            };
            return {
              ...action,
              ...{ callBack: () => setOpenUpload(actionModal) },
            };
          }
          default:
            return action;
        }
      });
  }, [actionsTopToolbar]);

  return (
    <AnimationFadeIn>
      <Table
        id={`${PagesID["projects.cost-detail"]}`}
        data={dataFormat || []}
        columns={filteredColumns || []}
        configurationTopToolbar={actionsToolbar}
        renderRowActions={customRenderRowActions}
        totalPages={dataTable?.metadata?.total_elements}
        modalTitle={t("hero-title")?.replace("-", "") || ""}
      />
      <ModalConfirmsCst
        open={!!openModalDelete}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenModalDelete(!openModalDelete);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => handleDelete()}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default AccountingCost;
